import { PlusIcon } from "@heroicons/react/24/solid";

import { Button, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ReportSectionFieldTypeEnums } from "@api/graphql/types-and-hooks";
import { getFieldTypeByContent } from "@components/SectionWidget/SectionWidget";
import { ClinicalReportAddRecommendationsBank } from "@pages/ClinicalReportDetails/Components/ClinicalReportAddRecommendationsBank";
import { useAdminData } from "@providers/AdminDataProvider";
import { useReport } from "@providers/ReportProvider";

interface ClinicalReportAddRecommendationsPanelProps {
  sectionId: string;
  onAddContent: (fieldType: ReportSectionFieldTypeEnums, rankOrder: number) => Promise<unknown>;
  newRankOrder: number;
  displayNoContentMessage?: boolean;
}

/**
 * A version of AddRecommendationsPanel that works with ClinicalReportDetailsProvider
 * instead of EvaluationProvider.
 */
export const ClinicalReportAddRecommendationsPanel: React.FC<ClinicalReportAddRecommendationsPanelProps> = ({
  sectionId,
  onAddContent,
  newRankOrder,
  displayNoContentMessage = false,
}) => {
  const { reportQuery } = useReport();
  const { isCatalightUser } = useAdminData();

  const handleAddContent = async () => {
    const fieldType = getFieldTypeByContent("Text");
    if (fieldType) {
      await onAddContent(fieldType, newRankOrder);
    }
  };

  return (
    <div className="flex justify-center">
      <div className="w-full pr-6 mt-4">
        {displayNoContentMessage ? (
          <div className="rounded-md mt-2 mb-4 py-4 w-full bg-background-secondary">
            <div className="flex justify-center w-full mb-2">
              <Paragraph displayType="loud">You have no recommendations added</Paragraph>
            </div>
            <div className="flex justify-center w-full">
              <Paragraph displayType="normal">You must have at least one recommendation selected to continue</Paragraph>
            </div>
          </div>
        ) : null}
        {isCatalightUser ? (
          <></>
        ) : (
          <div className="flex justify-end w-full space-x-2">
            <ClinicalReportAddRecommendationsBank
              sectionId={sectionId}
              onAdd={async () => {
                await reportQuery.refetch();
              }}
              newRankOrder={newRankOrder}
            />
            <Button
              text={
                <span className="flex items-center gap-1.5">
                  <PlusIcon className="h-4 w-4" />
                  Custom Recommendation
                </span>
              }
              variant="secondary"
              buttonStyle="border"
              size="md"
              onClick={() => handleAddContent()}
            />
          </div>
        )}
      </div>
    </div>
  );
};
